import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';
import '../styles/LocationMap.css';

const LocationMap = () => {
  const { t } = useTranslation();
  const [activeLocation, setActiveLocation] = useState('ceremony');
  const [showDirectionsMenu, setShowDirectionsMenu] = useState(false);
  const [customOrigin, setCustomOrigin] = useState('');

  const locations = {
    ceremony: {
      id: 'ceremony',
      title: t('location.weddingVenue'),
      name: 'Normafa Síház',
      address: 'Normafa út, 1121 Budapest, Hungary',
      time: t('location.venue.time'),
      description: t('location.venue.description'),
      features: [
        t('location.features.mountainViews'),
        t('location.features.indoorOutdoor'),
        t('location.features.parking'),
        t('location.features.photography'),
        t('location.features.historic')
      ],
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2695.594967654829!2d18.96399!3d47.5027237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741dfb5c89ff263%3A0xfcbd7ea737e5b616!2sNormafa%20S%C3%ADh%C3%A1z!5e0!3m2!1sen!2sus!4v1692000000000!5m2!1sen!2sus',
      color: '#9B8EC4'
    },
    itinerary: {
      id: 'itinerary',
      title: t('location.eveningItinerary'),
      color: '#7B7F8E',
      events: [
        { time: '3:00 PM', title: t('location.itinerary.ceremony'), description: t('location.itinerary.ceremonyDesc') },
        { time: '5:00 PM', title: t('location.itinerary.cocktailHour'), description: t('location.itinerary.cocktailDesc') },
        { time: '6:30 PM', title: t('location.itinerary.dinner'), description: t('location.itinerary.dinnerDesc') },
        { time: '8:00 PM', title: t('location.itinerary.firstDance'), description: t('location.itinerary.firstDanceDesc') },
        { time: '8:30 PM', title: t('location.itinerary.dancingCelebration'), description: t('location.itinerary.dancingDesc') },
        { time: '11:00 PM', title: t('location.itinerary.lateNightBites'), description: t('location.itinerary.lateNightDesc') },
      ]
    },
  };


  const currentLocation = locations[activeLocation] || locations['ceremony'];

  useEffect(() => {
    if (activeLocation !== 'ceremony' && activeLocation !== 'itinerary') {
      setActiveLocation('ceremony');
    }
  }, [activeLocation]);

  // Direction options
  const directionOptions = [
    {
      name: t('location.directions.fromCityCenter'),
      url: 'https://www.google.com/maps/dir/De%C3%A1k+Ferenc+t%C3%A9r,+Budapest,+Hungary/Normafa+S%C3%ADh%C3%A1z,+Normafa+%C3%BAt,+Budapest,+Hungary/@47.5027237,18.96399,17z',
      time: t('location.directions.cityCenterTime')
    },
    {
      name: t('location.directions.fromAirport'),
      url: 'https://www.google.com/maps/dir/Budapest+Ferenc+Liszt+International+Airport+(BUD),+Budapest,+Hungary/Normafa+S%C3%ADh%C3%A1z,+Normafa+%C3%BAt,+Budapest,+Hungary/@47.5027237,18.96399,17z',
      time: t('location.directions.airportTime')
    }
  ];

  const handleDirectionClick = (url) => {
    window.open(url, '_blank');
    setShowDirectionsMenu(false);
  };

  const handleCustomDirections = (e) => {
    e.preventDefault();
    const origin = customOrigin.trim();
    if (!origin) return;
    const url = `https://www.google.com/maps/dir/${encodeURIComponent(origin)}/Normafa+S%C3%ADh%C3%A1z,+Normafa+%C3%BAt,+Budapest,+Hungary/@47.5027237,18.96399,17z`;
    window.open(url, '_blank');
    setShowDirectionsMenu(false);
    setCustomOrigin('');
  };

  return (
    <motion.section
      id="location-map"
      className="location-map"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className="location-container">
        <motion.h2
          className="location-title"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {t('location.title')}
        </motion.h2>

        <motion.p
          className="location-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {t('location.subtitle')}
        </motion.p>

        {/* Location Navigation */}
        <motion.div
          className="location-nav"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          {Object.values(locations).map((location) => (
            <motion.button
              key={location.id}
              className={`location-nav-btn ${activeLocation === location.id ? 'active' : ''}`}
              onClick={() => setActiveLocation(location.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                borderColor: activeLocation === location.id ? location.color : 'transparent'
              }}
            >
              <span className="nav-label">{location.title}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Location Content */}
        <div className="location-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeLocation}
              className={`location-info ${activeLocation === 'itinerary' ? 'itinerary-layout' : ''}`}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              {activeLocation === 'itinerary' ? (
                /* Evening Itinerary - Coming Soon placeholder */
                <div className="itinerary-section">
                  <motion.div
                    className="details-card itinerary-card coming-soon-card"
                    style={{ borderTopColor: currentLocation.color }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h3 className="venue-name" style={{ color: currentLocation.color }}>
                      {t('location.eveningItinerary')}
                    </h3>
                    <p className="coming-soon-text">{t('location.detailsComingSoon')}</p>
                  </motion.div>
                </div>
              ) : (
                <>
                  {/* Location Details */}
                  <div className="location-details">
                    <motion.div
                      className="details-card"
                      style={{ borderTopColor: currentLocation.color }}
                    >
                      <div className="card-header">
                        <h3 className="venue-name" style={{ color: currentLocation.color }}>
                          {currentLocation.name}
                        </h3>
                        <div className="venue-time">{currentLocation.time}</div>
                      </div>

                      <div className="venue-address">
                        {currentLocation.address}
                      </div>

                      <p className="venue-description">{currentLocation.description}</p>

                      <div className="venue-features">
                        <h4>{t('location.featuresTitle')}</h4>
                        <div className="features-grid">
                          {currentLocation.features.map((feature, index) => (
                            <motion.span
                              key={index}
                              className="feature-tag"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.1 * index }}
                              style={{ borderColor: currentLocation.color }}
                            >
                              {feature}
                            </motion.span>
                          ))}
                        </div>
                      </div>

                    </motion.div>
                  </div>

                  {/* Map */}
                  <div className="location-map-container">
                    <motion.div
                      className="map-wrapper"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <iframe
                        src={currentLocation.mapEmbed}
                        width="100%"
                        height="400"
                        style={{ border: 0, borderRadius: '15px' }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map of ${currentLocation.name}`}
                      ></iframe>
                      <div className="map-overlay">
                        <div className="directions-container">
                          <motion.button
                            className="directions-btn"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowDirectionsMenu(!showDirectionsMenu)}
                            style={{ backgroundColor: currentLocation.color }}
                          >
                            {t('location.getDirections')}
                            <span className={`dropdown-arrow ${showDirectionsMenu ? 'open' : ''}`}>▼</span>
                          </motion.button>

                          <AnimatePresence>
                            {showDirectionsMenu && (
                              <motion.div
                                className="directions-dropdown"
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                              >
                                {directionOptions.map((option, index) => (
                                  <motion.button
                                    key={index}
                                    className="direction-option"
                                    onClick={() => handleDirectionClick(option.url)}
                                    whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <div className="option-content">
                                      <div className="option-details">
                                        <span className="option-name">{option.name}</span>
                                        <span className="option-time">{option.time}</span>
                                      </div>
                                    </div>
                                  </motion.button>
                                ))}

                                <form
                                  className="direction-custom"
                                  onSubmit={handleCustomDirections}
                                >
                                  <input
                                    type="text"
                                    className="direction-custom-input"
                                    placeholder={t('location.directions.customPlaceholder')}
                                    value={customOrigin}
                                    onChange={(e) => setCustomOrigin(e.target.value)}
                                    aria-label={t('location.directions.customLabel')}
                                  />
                                  <motion.button
                                    type="submit"
                                    className="direction-custom-btn"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    disabled={!customOrigin.trim()}
                                  >
                                    {t('location.directions.customGo')}
                                  </motion.button>
                                </form>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Additional Information */}
        <motion.div
          className="additional-info"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="info-cards">
            <motion.div
              className="info-card"
              whileHover={{ y: -5 }}
            >
              <h4>{t('location.info.transportationTitle')}</h4>
              <p>{t('location.info.transportationDesc')}</p>
            </motion.div>

            {/*
            <motion.div
              className="info-card"
              whileHover={{ y: -5 }}
            >
              <h4>{t('location.info.contactTitle')}</h4>
              <p>{t('location.info.contactDesc')}</p>
            </motion.div>

            <motion.div
              className="info-card"
              whileHover={{ y: -5 }}
            >
              <h4>{t('location.info.dressCodeTitle')}</h4>
              <p>{t('location.info.dressCodeDesc')}</p>
            </motion.div>
            */}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default LocationMap;